import { useDisplaySettings } from '@src/providers/DisplaySettingsProvider';
import { withPositionsAbsoluteSort } from '@src/utils/columns/absoluteSortEligibility';
import { createSecurityAwareGovernmentBondMarkPriceParams } from '@src/utils/columns/governmentBondMarkPrice';
import {
  getAgGridColId,
  getSecurityIdentifierColumns,
  isTalosUser,
  useDefaultColumns,
  useSecuritiesContext,
  type Column,
  type ColumnDef,
  type Leaves,
} from '@talos/kyoko';
import { compact } from 'lodash-es';
import { useMemo } from 'react';
import { useFeatureFlag, useUser } from '../../../../hooks';
import {
  positionAmountColumnParams,
  positionEquivalentAmountColumnParams,
  searchablePositionAssetColumnParams,
} from '../positionColumnRendering';
import type { UnifiedPositionColumn } from '../types';
import type { UnifiedPosition } from './UnifiedPosition';

type PositionLeaves = Leaves<UnifiedPosition>;

export const usePositionsUnifiedColumns = (defaultColumns: UnifiedPositionColumn[]): Column[] => {
  const user = useUser();
  const { homeCurrency } = useDisplaySettings();
  const { securitiesBySymbol } = useSecuritiesContext();
  const { showPositionsBlotterWarningColumn, enableSEDOLDisplay, enableGovernmentBond32nds } = useFeatureFlag();

  const columnDefinitions = useMemo(() => {
    return new Map<string, Column>(
      [
        ...compact([
          showPositionsBlotterWarningColumn && {
            id: 'reconWarning',
            type: 'reconWarning',
            field: 'reconWarning',
            aggregate: true,
            sortable: true,
          },
          {
            field: 'MarketAccount',
            type: 'marketAccount',
            sortable: true,
            initialSort: '-',
            enableRowGroup: true,
          },
          {
            field: 'market',
            type: 'market',
            initialHide: true,
            sortable: true,
            enableRowGroup: true,
          },
          {
            id: 'Asset',
            field: 'Asset',
            type: 'asset',
            sortable: true,
            title: 'Instrument',
            params: searchablePositionAssetColumnParams,
            enableRowGroup: true,
          },
          {
            id: 'Amount',
            field: 'NotionalAmount',
            type: 'size',
            title: 'Position',
            sortable: true,
            aggregate: true,
            initialWidth: 150,
            params: positionAmountColumnParams,
            description: 'Size of the position in currency terms.',
          },
          {
            type: 'size',
            field: 'Equivalent.Amount',
            title: `Position (${homeCurrency})`,
            params: positionEquivalentAmountColumnParams,
            sortable: true,
            initialHide: true,
            aggregate: true,
          },
          {
            field: 'Equivalent.Delta',
            type: 'size',
            title: `Delta (${homeCurrency})`,
            aggregate: true,
            initialWidth: 150,
            params: {
              currencyField: 'Equivalent.Currency' satisfies PositionLeaves,
              highlightNegative: true,
            },
            description:
              "Indicates how an instrument's price moves with its underlying, in home currency. It's calculated by Talos for spot and derivatives, and provided by the venue for options.",
          },
          {
            field: 'AvgCost',
            type: 'price',
            initialWidth: 150,
            params: {
              quoteCurrencyField: 'AvgCostCurrency' satisfies PositionLeaves,
            },
            description: 'Average cost for the instrument provided by the venue.',
          },
          {
            id: 'MarkPrice',
            title: 'Mark Price',
            field: 'markPriceUnified',
            type: 'price',
            initialWidth: 110,
            params: {
              quoteCurrencyField: 'markPriceCurrencyUnified' satisfies PositionLeaves,
              ...createSecurityAwareGovernmentBondMarkPriceParams<UnifiedPosition>(
                enableGovernmentBond32nds,
                securitiesBySymbol,
                data => data?.Asset,
                data => data?.AssetType
              ),
            },
            description: 'Most recent mark price for the instrument provided by the venue.',
          },
          {
            field: 'UnderlyingPrice',
            type: 'price',
            initialWidth: 130,
            params: {
              quoteCurrencyField: 'UnderlyingQuoteCurrency' satisfies PositionLeaves,
            },
            description: "Most recent fair price for the instrument's underlying provided by the venue.",
          },
          {
            field: 'balance',
            type: 'size',
            title: `Balance`,
            params: {
              highlightNegative: true,
              currencyField: 'Asset' satisfies PositionLeaves,
            },
            initialHide: true,
            aggregate: true,
            description: 'Spot balances, expressed in quantity of assets held.',
          },
          {
            field: 'balanceEquivalent',
            aggregate: true,
            type: 'size',
            title: `Balance (${homeCurrency})`,
            params: {
              highlightNegative: true,
              currencyField: 'Equivalent.Currency' satisfies PositionLeaves,
            },
            initialHide: true,
            description: 'Spot balances, in home currency.',
          },
          {
            field: 'Equivalent.UnrealizedPnL',
            type: 'size',
            title: `Unrealized PnL (${homeCurrency})`,
            aggregate: true,
            params: {
              currencyField: 'Equivalent.Currency' satisfies PositionLeaves,
              highlightNegative: true,
            },
            initialHide: true,
            description: 'Unrealized P&L for non-spot positions, converted to home currency.',
          },
          {
            type: 'size',
            title: `Equity`,
            field: 'venueEquity',
            aggregate: false,
            params: {
              currencyField: 'equityCurrency' satisfies PositionLeaves,
              highlightNegative: true,
            },
            sortable: true,
            description: 'Exchange published equity.',
          },
          {
            type: 'size',
            title: `Equity (${homeCurrency})`,
            field: 'venueEquityEquivalent',
            aggregate: false,
            params: {
              currencyField: 'Equivalent.Currency' satisfies PositionLeaves,
              highlightNegative: true,
            },
            sortable: true,
            description: 'Exchange published equity, in home currency.',
          },
          {
            type: 'size',
            title: `Equity (Talos)`,
            field: 'talosEquity',
            aggregate: true,
            params: {
              currencyField: 'equityCurrency' satisfies PositionLeaves,
              highlightNegative: true,
            },
            sortable: true,
            description:
              'Talos calculated equity. Spot balances plus unrealized P&L from derivatives positions, in instrument terms.',
          },
          {
            type: 'size',
            title: `Equity (Talos) (${homeCurrency})`,
            field: 'talosEquityEquivalent',
            aggregate: true,
            params: {
              currencyField: 'Equivalent.Currency' satisfies PositionLeaves,
              highlightNegative: true,
            },
            sortable: true,
            description:
              'Talos calculated equity. Spot balances plus unrealized P&L from derivatives positions, in home currency.',
          },
          {
            type: 'size',
            title: `Initial Margin (${homeCurrency})`,
            field: 'Equivalent.initialMargin',
            aggregate: false,
            params: {
              currencyField: 'Equivalent.Currency' satisfies PositionLeaves,
              highlightNegative: true,
            },
            sortable: true,
            description: 'Exchange published initial margin requirement for derivatives positions, in home currency.',
          },
          {
            type: 'size',
            title: `Maintenance Margin (${homeCurrency})`,
            field: 'Equivalent.maintenanceMargin',
            aggregate: false,
            params: {
              currencyField: 'Equivalent.Currency' satisfies PositionLeaves,
              highlightNegative: true,
            },
            sortable: true,
            initialWidth: 200, // don't wanna cut off the maintenance margin header label, looks bad
            description:
              'Exchange-published maintenance margin requirement for derivatives positions, in home currency.',
          },
          {
            type: 'text',
            title: 'Margin Type',
            field: 'marginReportingType',
            initialHide: true,
            initialWidth: 100,
            enableRowGroup: true,
            description:
              'The margin classification reported by the venue, specifying if it applies at the position level (per currency) or the account level.',
          },
          {
            field: 'LiquidationPrice',
            type: 'price',
            initialWidth: 130,
            params: {
              assetField: 'Asset' satisfies PositionLeaves,
            },
            description: 'An estimated price at which the venue would initiate the liquidation process.',
          },
          {
            field: 'AutoDeleverageRiskLevel',
            type: 'text',
            title: 'ADL Risk Level',
            initialHide: true,
            initialWidth: 100,
            description: 'Auto-deleveraging (ADL) risk level for this position.',
          },
          {
            field: 'Leverage',
            type: 'number',
            title: 'Leverage',
            initialHide: true,
            initialWidth: 100,
            params: {
              currency: 'x',
            },
            description: 'Leverage for this position as reported by the venue.',
          },
          {
            field: 'RiskLimit',
            type: 'size',
            title: 'Risk Limit',
            initialHide: true,
            initialWidth: 150,
            params: {
              currencyField: 'RiskLimitCurrency' satisfies PositionLeaves,
            },
            description: 'Risk limit for this position as reported by the venue.',
          },
          {
            field: 'elpVsMp',
            type: 'percent',
            title: 'ELP vs MP (%)',
            initialWidth: 100,
            params: { increment: '0.01', round: true },
            description:
              'Percentage move in price needed from current mark levels to hit the estimated liquidation levels for a position (Liquidation Price minus Mark Price, divided by Mark Price). Talos will calculate this value based on the data provided by the venues.',
          },
          {
            type: 'text',
            field: 'marketAccountGroup',
            id: 'marketAccountGroup',
            title: 'Group',
            initialHide: true,
            sortable: true,
            enableRowGroup: true,
            description: "The Group property of the position's Market Account, if any.",
          },
          {
            field: 'Amount',
            id: 'position-qty',
            type: 'size',
            title: 'Position Qty',
            params: {
              showInTermsOfContracts: true,
              highlightNegative: true,
              currencyField: 'Asset' satisfies PositionLeaves,
              trimTrailingZeroes: true,
            },
            initialHide: true,
            description: 'Number of derivative contracts or size of spot position.',
          },
          {
            id: 'underlying',
            field: 'underlying',
            type: 'asset',
            params: {
              colorful: true,
              showDescription: false,
            },
            title: 'Underlying',
            enableRowGroup: true,
          },
          {
            id: 'expiry',
            type: 'positionExpiry',
            title: 'Expiry',
            sortable: true,
            initialWidth: 80,
          },
          {
            field: 'Delta',
            type: 'delta',
            aggregate: true,
            initialWidth: 150,
            params: {
              currencyField: 'DeltaCurrency' satisfies keyof UnifiedPosition,
            },
            description:
              "Indicates how an instrument's price moves with its underlying. It's calculated by Talos for spot and derivatives, and provided by the venue for options.",
          },
          {
            field: 'Gamma',
            type: 'gamma',
            initialWidth: 80,
            description:
              "Gamma measures the rate of change of option's delta with respect to changes in the underlying asset's price.",
          },
          {
            field: 'Vega',
            type: 'vega',
            initialWidth: 60,
            description:
              "Vega measures the rate of change (in USD) of an option's price in response to a +1 vol point move in implied volatility.",
          },
          {
            field: 'Theta',
            type: 'theta',
            initialWidth: 60,
            description:
              'Theta measures the change (in USD) in the price of an option for a one-day decrease in its time to expiration.',
          },
          {
            field: 'Rho',
            type: 'rho',
            initialWidth: 60,
            description:
              'Rho measures the rate of change (in USD) of an option’s price in response to a 1% shift in interest rate.',
          },

          {
            field: 'AssetType',
            title: 'Product Type',
            type: 'productTypeField',
            initialHide: true,
            initialWidth: 130,
            enableRowGroup: true,
          },
          {
            field: 'UnrealizedPnL',
            type: 'size',
            title: 'Unrealized PnL',
            params: {
              currencyField: 'PnLCurrency' satisfies PositionLeaves,
              highlightNegative: true,
            },
            initialHide: true,
            description:
              'Unrealized P&L provided by the venues for non-spot positions. The type of P&L (Life-To-Date or Session) is determined by the venue.',
          },
          {
            field: 'CumFees',
            type: 'size',
            title: 'Fees',
            params: {
              currencyField: 'FeeCurrency' satisfies PositionLeaves,
            },
            aggregate: true,
          },
          {
            field: 'Equivalent.CumFees',
            type: 'size',
            title: `Fees (${homeCurrency})`,
            params: {
              currencyField: 'Equivalent.Currency' satisfies PositionLeaves,
            },
            aggregate: true,
            initialHide: true,
          },
          {
            field: 'FundingPnL',
            type: 'size',
            title: 'Funding PnL',
            aggregate: true,
            params: {
              currencyField: 'FundingPnLCurrency' satisfies PositionLeaves,
              highlightNegative: true,
            },
            description: 'Cumulative funding payment paid or received in settlement currency.',
          },
          {
            field: 'Equivalent.FundingPnL',
            type: 'size',
            title: `Funding PnL (${homeCurrency})`,
            params: {
              currencyField: 'Equivalent.Currency' satisfies PositionLeaves,
              highlightNegative: true,
            },
            aggregate: true,
            initialHide: true,
            description: 'Cumulative funding payment paid or received in home currency.',
          },
          {
            field: 'RealizedPnL',
            type: 'size',
            title: 'Realized LTD PnL',
            params: {
              currencyField: 'PnLCurrency' satisfies PositionLeaves,
              highlightNegative: true,
            },
          },
          {
            field: 'TotalPnL',
            type: 'size',
            title: 'Total LTD PnL',
            aggregate: true,
            params: {
              currencyField: 'PnLCurrency' satisfies PositionLeaves,
              highlightNegative: true,
            },
          },
          {
            field: 'AvgCostCurrency',
            type: 'currency',
            initialHide: true,
          },
          {
            field: 'PnLCurrency',
            type: 'currency',
            title: 'PnL Currency',
            initialHide: true,
          },
          {
            type: 'size',
            title: `Initial Margin`,
            field: 'initialMargin',
            aggregate: false,
            params: {
              currencyField: 'equityCurrency' satisfies PositionLeaves,
              highlightNegative: true,
            },
            sortable: true,
            description:
              'Exchange published initial margin requirement for derivatives positions, in instrument terms.',
          },
          {
            type: 'size',
            title: `Maintenance Margin`,
            field: 'maintenanceMargin',
            aggregate: false,
            params: {
              currencyField: 'equityCurrency' satisfies PositionLeaves,
              highlightNegative: true,
            },
            sortable: true,
            initialWidth: 200, // don't wanna cut off the maintenance margin header label, looks bad
            description:
              'Exchange-published maintenance margin requirement for derivatives positions, in instrument terms.',
          },
          {
            field: 'LastUpdateTime',
            type: 'date',
            initialHide: true,
          },
          {
            field: 'Status',
            type: 'text',
            initialHide: true,
          },
          {
            field: 'OutstandingBuy',
            type: 'size',
            params: { currencyField: 'Asset' satisfies PositionLeaves, showCurrencyForNullPosition: false },
          },
          {
            field: 'OutstandingSell',
            type: 'size',
            params: { currencyField: 'Asset' satisfies PositionLeaves, showCurrencyForNullPosition: false },
          },
          {
            type: 'size',
            field: 'Equivalent.OutstandingBuy',
            title: `Outstanding Buy (${homeCurrency})`,
            params: { currencyField: 'Equivalent.Currency' satisfies PositionLeaves, highlightNegative: true },
            sortable: true,
            initialHide: true,
            aggregate: true,
          },
          {
            type: 'size',
            field: 'Equivalent.OutstandingSell',
            title: `Outstanding Sell (${homeCurrency})`,
            params: { currencyField: 'Equivalent.Currency' satisfies PositionLeaves, highlightNegative: true },
            sortable: true,
            initialHide: true,
            aggregate: true,
          },
          isTalosUser(user) && {
            field: 'PositionSource',
            type: 'text',
            initialHide: true,
          },
        ] satisfies (ColumnDef<UnifiedPosition> | false)[]),
        ...getSecurityIdentifierColumns({ symbolField: 'Asset', includeSedol: enableSEDOLDisplay }),
      ].map(c => [getAgGridColId(c), withPositionsAbsoluteSort(c)])
    );
  }, [
    enableGovernmentBond32nds,
    enableSEDOLDisplay,
    showPositionsBlotterWarningColumn,
    homeCurrency,
    securitiesBySymbol,
    user,
  ]);

  return useDefaultColumns(defaultColumns, columnDefinitions);
};
